import { InfoOutlineIcon } from "@chakra-ui/icons";
import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import { formatCurrency } from "@umami/chains";
import { type LiFiQuoteResponse } from "@umami/swap";

import { useColor } from "@/styles/useColor";
import { formatLiFiFeeBreakdown, parseLiFiFeeBreakdown } from "@/utils/lifiFeeBreakdown";

interface NetworkFeeDisplayProps {
  feeDisplay: {
    amount: number;
    symbol: string;
    usdValue: number;
  } | null;
  isLoading: boolean;
  provider?: string;
  lifiQuote?: LiFiQuoteResponse | null;
}

export const NetworkFeeDisplay = ({
  feeDisplay,
  isLoading,
  provider,
  lifiQuote,
}: NetworkFeeDisplayProps) => {
  const color = useColor();

  const formatNetworkFee = () => {
    if (isLoading) {
      return "Loading...";
    }

    if (!feeDisplay) {
      return "No fee";
    }

    // For LiFi quotes, show only USD value
    if (feeDisplay.symbol === "USD") {
      return formatCurrency(feeDisplay.usdValue);
    }

    // For other quotes, show token amount and USD value
    return `${feeDisplay.amount.toFixed(6)} ${feeDisplay.symbol} (${formatCurrency(feeDisplay.usdValue)})`;
  };

  const getTooltipContent = () => {
    if (provider === "LiFi" && lifiQuote) {
      return (
        <Text whiteSpace="pre-line">
          {formatLiFiFeeBreakdown(parseLiFiFeeBreakdown(lifiQuote))}
        </Text>
      );
    }

    return "This fee covers gas costs and other protocol-level expenses required to complete the transaction on the network.";
  };

  return (
    <Flex alignItems="center" justifyContent="space-between" width="100%">
      <Flex alignItems="center" gap="2">
        <Text color={color("500")} fontWeight="600">
          Network Fee
        </Text>
        <Tooltip hasArrow label={getTooltipContent()} placement="top">
          <Box as="span">
            <InfoOutlineIcon width="16px" height="16px" color={color("500")} />
          </Box>
        </Tooltip>
      </Flex>
      <Text color={color("700")}>{formatNetworkFee()}</Text>
    </Flex>
  );
};
